import { ITask } from 'Core/TaskAdapter'
import {
    AttributeDefinitionType,
    ContainerType,
    TaskConfigType,
    CustomInterfaceDataType,
} from 'Types/Tasks.types'
import { createReactElement } from 'shared/utils/react.utils'
import { Studio } from 'Core/Studio'
import { CommonTask } from 'Tasks/CommonTask/CommonTask'
import { InterfaceContainerStrategy } from 'Core/StateContainerCore/ContainerStrategies/InterfaceContainerStrategy'
import { TaskInterface } from 'shared/express-interface/core/TaskInterface'
import { CustomInterfaceTaskUi } from './UI/CustomInterfaceTaskUI'
import { ModuleExtension } from 'shared/express-interface/core/PythonModule'
import { ViolationLevel, ViolationType } from 'shared/violations/types'
import { getBearerToken } from 'shared/auth/auth.utils'
import { InterfaceConfig } from 'shared/express-interface/core/types'

export class CustomInterfaceTask
    extends CommonTask<ContainerType.CustomInterface, CustomInterfaceDataType>
    implements ITask<TaskConfigType<ContainerType.CustomInterface, CustomInterfaceDataType>>
{
    public taskInterface: TaskInterface
    constructor(protected studio: Studio) {
        const interfaceContainerStrategy = new InterfaceContainerStrategy()
        super(studio, interfaceContainerStrategy)
        interfaceContainerStrategy.setContext({
            task: this,
        })

        this.studio.blockEngine.setEditable(!this.isReadOnly)
        const initialState =
            this.taskState.getDraftOutputContainer()?.data.data.response.state.value

        const interfaceConfig = (this.studio.taskConfig.assignment.task.custom_interface?.config ??
            {}) as InterfaceConfig
        const inputData =
            this.studio.taskConfig.assignment.task.input_container.data.data.custom_interface
                ?.content.value
        this.taskInterface = new TaskInterface({
            config: interfaceConfig,
            blockEngine: this.studio.blockEngine,
            initialState,
            inputData: inputData ?? {},
        })

        const violationsExtension = this.createViolationsExtension(this.studio.inputManager)
        this.taskInterface.getPythonModule().registerExtension('violation', violationsExtension)

        const environmentExtension = this.createEnvironmentExtension()
        this.taskInterface.getPythonModule().registerExtension('environment', environmentExtension)

        const userExtension = this.createUserExtension()
        this.taskInterface.getPythonModule().registerExtension('user', userExtension)

        this.taskInterface.getStateStore().subscribe(() => {
            this.autoSaveTask()
        })
    }

    public override setReadOnly = (isReadOnly: boolean) => {
        super.setReadOnly(isReadOnly)
        this.studio.blockEngine.setEditable(!isReadOnly)
    }

    public async initialize(
        config: TaskConfigType<ContainerType.CustomInterface, CustomInterfaceDataType>
    ): Promise<void> {
        const blocks = await this.taskInterface.build()
        this.studio.workbench.enqueueUiUpdate(async workbenchUi => {
            this.ui = createReactElement(CustomInterfaceTaskUi, {
                blockEngine: this.studio.blockEngine,
                blocks: blocks,
            })
            this.addCommonWorkbenchComponents(workbenchUi, config)

            // add rate/review if attributes are present
            if (this.hasEditableAttributes(config)) {
                this.attributeManager?.useAttributeGroup({
                    type: AttributeDefinitionType.QUALITY_EVALUATION,
                    groupKey: 'quality-evaluation',
                    attributes: config.assignment.task.attributes.filter(
                        attr => attr.type === AttributeDefinitionType.QUALITY_EVALUATION
                    ),
                    config: { useInCollection: true },
                })
                this.attributeManager?.useAttributeGroup({
                    type: AttributeDefinitionType.EXTRA_INFO,
                    groupKey: 'extra-info',
                    attributes: config.assignment.task.attributes.filter(
                        attr => attr.type === AttributeDefinitionType.EXTRA_INFO
                    ),
                    config: { useInCollection: true },
                })
                workbenchUi.rightSidebar.addDivider()
            }
        })
    }

    private createEnvironmentExtension(): ModuleExtension {
        const step = this.studio.taskConfig.assignment.step
        const jobId = this.studio.taskConfig.jobId
        const pluginAssociations = this.studio.studioConfig.pluginsConfig
        return {
            stepType: step?.type,
            stepId: step?.id,
            jobId: jobId,
            pluginAssociations,
        }
    }

    private createViolationsExtension(inputManager: Studio['inputManager']): ModuleExtension {
        return {
            add: (id: string, message: string, type: ViolationLevel = 'error') => {
                const violation: ViolationType = {
                    id: String(id),
                    message: String(message),
                    type,
                }
                inputManager.addViolation(violation)
            },

            remove: (id: string, level?: ViolationLevel) => {
                inputManager.removeViolation(String(id), level)
            },

            has: (id: string): boolean => {
                return inputManager.checkViolationByID(String(id))
            },

            count: (level?: ViolationLevel): number => {
                return inputManager.getViolationCount(level)
            },

            getAll: () => {
                return inputManager.getViolation()
            },

            getErrorAndWarningCount: () => {
                return inputManager.getErrorAndWarningCount()
            },
        }
    }

    private createUserExtension(): ModuleExtension {
        const userId = this.studio.taskConfig.assignment.user_id
        return {
            id: userId,
            token: getBearerToken(),
        }
    }

    public destroy = () => {
        this.taskInterface.getStateStore().unsubscribeAll()
        this.taskInterface.destroy()
    }
}
